import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import Breadcrumb, { Crumb } from "../../../../components/common/Breadcrumbs";
import { useNavigate, useParams } from "react-router-dom";
import { useGetAllStatesQuery } from "../../../data/states/statesApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import { OptionType } from "../../../../helpers/utils/StyleUtils";
import AuditableInfoButton from "../../../../components/common/form/AuditableInfoButton";
import Form from "../../../../components/common/form/Form";
import SelectField from "../../../../components/common/form/SelectField";
import { createOptionListForSelectTag, showToast } from "../../../../helpers/utils/Utils";
import ButtonTray from "../../../../components/common/form/ButtonTray";
import { Routes as AppRoutes } from "../../../../routes";
import { useGetMetroAreaByStateAbbrQuery } from "../../../data/metroAreas/metroAreasApi";
import { useGetEnumsQuery } from "../../../../app/globalApi";
import {useGetAllTenantsQuery} from "../../../customer/tenantApi";
import {
    useCreateAutoHoaMutation,
    useDeleteAutoHoaByIdMutation,
    useGetAutoHoaByIdQuery,
    useUpdateAutoHoaMutation
} from "../autoHoa";
import { useGetAllCountyByStateAbbrQuery } from "../../../data/counties/countiesApi";
import ConfirmationModal from "../../../../components/layout/modal/ConfirmationModal";
import ModalButton from "../../../../components/common/form/ModalButton";
import { FaTrash } from "react-icons/fa";
import IconButton from "../../../../components/common/form/IconButton";
import { hasPermission } from "../../../../helpers/utils/AccessControlUtils";

interface Props {
    crumbs: Crumb[];
    title: string;
    btnText: string;
    isReadOnly?: boolean;
}

const AutoHoaForm: FC<Props> = ({ crumbs, title, btnText, isReadOnly = false }) => {

    const navigate = useNavigate();
    let { hoaId } = useParams();

    const [createAutoHoa, createAutoHoaApiResponse] = useCreateAutoHoaMutation();
    const [updateAutoHoa, updateAutoHoaApiResponse] = useUpdateAutoHoaMutation();
    const autoHoaByIdApi = useGetAutoHoaByIdQuery(hoaId ?? skipToken);
    const [autoHoaData, setAutoHoaData] = useState<any>({state:{value:"TX",label:"Texas"}});
    const statesApiResult = useGetAllStatesQuery("");
    const countiesApiResult = useGetAllCountyByStateAbbrQuery(autoHoaData?.state?.value ?? skipToken);
    const metroApiResult = useGetMetroAreaByStateAbbrQuery(autoHoaData?.state?.value ?? skipToken);
    const { data: enums } = useGetEnumsQuery("");
    const tenantData = useGetAllTenantsQuery("");
    const [deleteAutoHoa, { isLoading }] = useDeleteAutoHoaByIdMutation();

    useEffect(() => {
        document.title = "Auto HOA"
    });

    useEffect(() => {
        let obj = autoHoaByIdApi?.data;
        if (obj) {
            setAutoHoaData({
                ...obj,
                state: obj?.msa?.state
                    ? { label: obj?.msa?.state?.stateName, value: obj?.msa?.state?.stateAbbreviation }
                    : (
                        obj?.county?.state
                            ? { label: obj?.county?.state?.stateName, value: obj?.county?.state?.stateAbbreviation }
                            : null
                    ),
                metroAreas: obj.msa
                    ? { label: obj.msa?.name, value: obj.msa?.id }
                    : null,
                county: obj.county
                    ? { label: obj.county?.countyName, value: obj.county?.id }
                    : null,
                productType: obj.productType
                    ? { label: obj.productType, value: obj.productType }
                    : null,
                tenant: obj.tenant
                    ? { label: obj.tenant, value: obj.tenant }
                    : null
            });
        }
    }, [autoHoaByIdApi?.data]);


    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const input = {
            id: hoaId,
            msa: autoHoaData?.metroAreas != null ? {
                id: autoHoaData?.metroAreas?.value
            } : null,
            county: autoHoaData?.county != null ? {
                id: autoHoaData?.county?.value
            } : null,
            productType: autoHoaData?.productType?.value,
            tenant: autoHoaData?.tenant?.value
        }
        e.preventDefault();
        if (input) {
            if (input.county === null && input.msa === null) {
                return showToast("Either County or Metro Area should be selected");
            }
            if (input.county !== null && input.msa !== null) {
                return showToast("Both County and Metro Area cannot be selected");
            }

            const createOrUpdateAutoHoa = async () => {
                if (btnText === "Create") {
                    return await createAutoHoa(input).unwrap();
                } else {
                    return await updateAutoHoa({
                        id: hoaId,
                        requestBody: input,
                    }).unwrap();
                }
            };

            await handleRTKQuery(createOrUpdateAutoHoa, (response: any) => {
                if (response?.success || response?.data?.success) {
                    setAutoHoaData({});
                    navigate(AppRoutes.AutoHoa.path);
                }
            });
        }
    }

    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const { name, value } = "target" in e ? e.target : e;

        if (name === "state") {
            setAutoHoaData({
                ...autoHoaData,
                [name]: value,
                county: null,
                metroAreas: null,
            })
        } else if (name === "county") {
            setAutoHoaData({
                ...autoHoaData,
                [name]: value,
                metroAreas: null,
            })
        } else if (name === "metroAreas") {
            setAutoHoaData({
                ...autoHoaData,
                [name]: value,
                county: null,
            })
        } else {
            setAutoHoaData({
                ...autoHoaData,
                [name]: value
            })
        }

    }


    const handleDelete = async () => {
        if (!hoaId) return;
        try {
            await deleteAutoHoa(hoaId).unwrap();
            navigate(AppRoutes.AutoHoa.path)
        } catch (error) {
            console.error("Failed to delete HOA:", error);
        }
    };
    return (
        <>
            <div className="px-4 py-2">

                <div className={"justify-between sm:block md:flex lg:flex"}>
                    <Breadcrumb crumbs={crumbs} />
                    <div className={"justify-between sm:block md:flex lg:flex"}>
                        {hoaId && hasPermission(["AUTOHOA-D"]) && (
                            <ModalButton
                                triggerButton={
                                    <IconButton icon={<FaTrash />}
                                        title={"Auditable Info"} hasBackground={true} className="mr-2" />
                                }
                                modal={
                                    <ConfirmationModal
                                        show={true}
                                        setShow={() => {
                                        }}
                                        title={"Delete Auto Hoa"}
                                        bodyMessage={"Are you sure you want to delete this Auto Hoa?"}
                                        onSubmit={handleDelete}
                                        isLoading={isLoading}
                                    />
                                }
                            />
                        )}
                        {
                            hasPermission(["AUDIT-V"]) &&
                            <AuditableInfoButton obj={autoHoaByIdApi?.data} />}
                    </div>
                </div>

                <div className="flex justify-center mt-3">
                    <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                        <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-5">{title}</h3>
                        <Form onSubmit={handleSubmit} fields={<>
                            <div className="grid grid-cols-12">

                                <SelectField
                                    placeholder='Select State...'
                                    value={autoHoaData?.state}
                                    options={createOptionListForSelectTag(statesApiResult?.data, "stateName", "stateAbbreviation")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "state", value: selectedOption })
                                    }}
                                    label={"State"}
                                    required={true}
                                    isDisabled={isReadOnly}
                                />

                                <SelectField
                                    placeholder='Select Metro Area...'
                                    value={autoHoaData?.metroAreas}
                                    options={createOptionListForSelectTag(metroApiResult?.data, "name", "id")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "metroAreas", value: selectedOption })
                                    }}
                                    label={"Metro Area"}
                                    isDisabled={metroApiResult?.data?.length === 0 || !autoHoaData?.state || autoHoaData?.county || isReadOnly}
                                />
                                <SelectField
                                    placeholder='Select County...'
                                    value={autoHoaData?.county}
                                    options={createOptionListForSelectTag(countiesApiResult?.data, "countyName", "id")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "county", value: selectedOption })
                                    }}
                                    label={"Counties"}
                                    isDisabled={!autoHoaData?.state || autoHoaData?.metroAreas || isReadOnly}
                                    isLoading={!autoHoaData?.state}

                                />


                                <SelectField
                                    placeholder='Select Product Type...'
                                    value={autoHoaData?.productType}
                                    options={createOptionListForSelectTag(enums?.["ProductType"] && enums?.["ProductType"].filter((p: any) => p.constant.includes("HOA")), "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "productType", value: selectedOption })
                                    }}
                                    label={"Product Type"}
                                    required={true}
                                    isDisabled={isReadOnly}
                                />
                                <SelectField
                                    placeholder='Select Tenant...'
                                    value={autoHoaData?.tenant}
                                    options={createOptionListForSelectTag(tenantData?.data, "name", "name")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "tenant", value: selectedOption })
                                    }}
                                    label={"Tenant"}
                                    required={true}
                                    isDisabled={isReadOnly}
                                />
                            </div>

                            {isReadOnly === false &&
                                <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    </div>
                                    <div className="relative col-span-9">
                                        <div className={"mt-4 flex"}>
                                            <ButtonTray
                                                buttons={[
                                                    {
                                                        buttonProps: {
                                                            btnText: "Cancel",
                                                            onClick: () => navigate(AppRoutes.AutoHoa.path),
                                                            type: "cancel",
                                                        },
                                                        buttonType: "button"
                                                    },
                                                    {
                                                        buttonProps: {
                                                            btnText: "Submit",
                                                            type: "submit",
                                                            isLoading: (createAutoHoaApiResponse.isLoading || updateAutoHoaApiResponse.isLoading),

                                                        },
                                                        buttonType: "button",
                                                    }
                                                ]}
                                                align="center"
                                                gap={2}
                                            />
                                        </div>
                                    </div>
                                </div>
                            }
                        </>}
                        />

                    </div>
                </div>
            </div>
        </>
    )
}

export default AutoHoaForm;